/**
 * SideNav
 * Left-side navigation bar for the web revamp dashboard.
 * Displays the Instarem logo and nav items per the Figma design (ICFE-4921).
 */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { MobileDivider, NavList, SideNavContainer } from './SideNav.styles';
import {
  APPROVED,
  AUTHORIZATIONS,
  CORPORATE,
  APP_ONLY_CORRIDOOR_WELCOME_URL,
  DASHBOARD_URL,
  DEVICE_MOBILE,
  HEADER_FOOTER_IGNORED_URLS,
  TRANSFER_MONEY_URL,
  INDIVIDUAL,
  MULTIPLE_TRANSACTION_URL,
  FLOW_TYPE,
  CONSENT_PERMISSION_URL,
  HK,
  MANAGE_PAYMENT_OPTION_URL,
  REFERRAL_URL,
  SUBSCRIPTION_URL,
  USER_PROFILE_URL,
  SG,
  UK,
  US,
  AU,
  EU_REGION
} from '@Utils/constants';
import {
  getUserFlowReducer,
  selectUiTriggerStateReducer,
  selectUserSession,
  selectUserStatusReducer
} from '@Redux/selectors';
import { toggleSidebar } from '@Redux/actions/ui-trigger/ui-trigger-action';
import { AppDispatch, UserFlowReducer, UserStatusReducer } from '../../../../store.type';
import { useNavItems } from './hooks/useNavItems';
import { logEventV2, getPageName } from '@Utils/events/logUserEvents';
import { toggleNewCouponCode } from '@Redux/actions/transaction';
import { clearDashboardCalculatorData } from '@Redux/actions/dashboard';
import { resetTransactionDetails } from '@Redux/actions/common';
import { changeScreen, destroyUserFlowAction } from '@Redux/actions/user-flow';
import { USER_FLOW_JUMP_TO } from '@Redux/action-contants';
import { NavItem } from './SideNav.types';
import {
  EVENT_ROUTE_KEYS,
  EventButtonType,
  EventNamesV2,
  FLOW_TYPE as EVENT_FLOW_TYPE
} from '@Utils/events/eventsConstants';
import { shouldShowPaymentOptionTab } from '@Utils/library';
import { useLogout, LOGOUT_LABEL } from '../../../../hooks/useLogout';
import {
  LOGOUT_ICON,
  MANAGE_PAYMENT_OPT_ICON,
  RECIPIENT_ICON,
  REFERRAL_SMALL_ICON,
  SEND_MONEY_ICON,
  SUBSCRIPTION_ICON
} from '@Images';

const SideNav: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const navItems = useNavItems();
  const dispatch = useDispatch<AppDispatch>();
  const {
    account_type: userAccountType,
    status,
    country_code,
    nonHardMigratedSMEFlow
  } = useSelector(selectUserStatusReducer) as UserStatusReducer & { nonHardMigratedSMEFlow?: any };
  const {
    timeline,
    screens,
    currentScreen: currentScreenNormalFlow
  } = useSelector(getUserFlowReducer) as UserFlowReducer;
  const { device } = useSelector(selectUiTriggerStateReducer);
  const user_session = useSelector(selectUserSession) as any;
  const logout = useLogout();

  if (!HEADER_FOOTER_IGNORED_URLS.includes(pathname)) {
    const isHeaderDisabled = /signin|signup|invite\/|password|logout|feedback\//gi.test(pathname);
    if (isHeaderDisabled || pathname === '/') return null;
  }

  const { account_type, additional_info } = user_session;
  const hard_sme_migrated = account_type === CORPORATE && additional_info?.hard_sme_migrated;
  const isAppOnlyCorridorRoute = window.location.href.includes(APP_ONLY_CORRIDOOR_WELCOME_URL);
  const showManagePayment = [HK, US, SG, UK, AU, ...EU_REGION];
  const showManagePaymentCorporate = [US];
  const paymentOptionTabVisible = shouldShowPaymentOptionTab(country_code);
  const authorized =
    (AUTHORIZATIONS as any)[status] &&
    (AUTHORIZATIONS as any)[status].find((text: string) => text === USER_PROFILE_URL);

  let smeFlow = false;
  if (nonHardMigratedSMEFlow?.isFeatureEnabled && account_type === CORPORATE) {
    const { data } = nonHardMigratedSMEFlow;
    smeFlow = data.includes(country_code) && status === APPROVED;
  }

  const logCTEvents = (ctaName: string, flowType: string) => {
    logEventV2(EventNamesV2.CTA_CLICKED, {
      type: EventButtonType.BUTTON,
      pageName: getPageName(),
      ctaName,
      flowType
    });
  };

  const onAccountLinkClick = (targetUrl?: string) => {
    dispatch(toggleSidebar());
    dispatch(destroyUserFlowAction());
    if (targetUrl === DASHBOARD_URL) {
      dispatch(toggleNewCouponCode(false));
      dispatch(clearDashboardCalculatorData());
    }
  };

  const handleOnSendMoneyClick = () => {
    // 1. log event
    // toggle new coupon code and clear calculator data use actions and dispatch
    // get the screens, timeline, and currentScreen from userFlowTimeline
    // logic
    // this will fire on send-money menu item click and toggle new coupon code and reset the calculator data

    const analyticsData = {
      currentScreen: currentScreenNormalFlow,
      type: EventButtonType.BUTTON,
      accountType: userAccountType,
      pageName: EVENT_ROUTE_KEYS.SendMoney,
      ctaName: 'SendMoney',
      flowType: FLOW_TYPE.TRANSACTION
    };
    logEventV2(EventNamesV2.CTA_CLICKED, analyticsData);
    dispatch(toggleNewCouponCode(false));
    dispatch(clearDashboardCalculatorData());
    if (pathname === TRANSFER_MONEY_URL && currentScreenNormalFlow === 'send-money') {
      return;
    } else if (pathname === TRANSFER_MONEY_URL && currentScreenNormalFlow !== 'send-money') {
      dispatch(resetTransactionDetails());
      dispatch(changeScreen(USER_FLOW_JUMP_TO, 'send-money', screens, timeline));
    } else {
      dispatch(resetTransactionDetails());
      if (userAccountType === INDIVIDUAL) {
        navigate(TRANSFER_MONEY_URL);
      } else {
        navigate(MULTIPLE_TRANSACTION_URL);
      }
    }
  };

  const handleOtherItemClick = (item: NavItem) => {
    const analyticsData = {
      item,
      currentScreen: currentScreenNormalFlow,
      accountType: userAccountType,
      type: EventButtonType.BUTTON,
      pageName: item.routeKey,
      ctaName: item.key,
      flowType: item.flowType
    };
    logEventV2(EventNamesV2.CTA_CLICKED, analyticsData);
    // we do not want to add other logic like reset calculator data and toggle new coupon code because
    // it is already handled on handleOnSendMoneyClick method
    dispatch(destroyUserFlowAction());
  };

  const handleMenuItemClick = (item: NavItem) => {
    if (device === DEVICE_MOBILE) {
      dispatch(toggleSidebar());
    }
    switch (item.key) {
      case 'send-money': {
        handleOnSendMoneyClick();
        break;
      }
      default:
        handleOtherItemClick(item);
        break;
    }
  };

  if (pathname === CONSENT_PERMISSION_URL) return null;
  return (
    <SideNavContainer>
      <NavList>
        {navItems.map((item) => {
          const isActive = pathname.startsWith(item.route);
          const isMailto = item.route.startsWith('mailto:');
          const className = `nav-list__item${isActive ? ' active' : ''}`;
          const content = (
            <>
              <img alt={item.label} src={isActive ? item.activeIcon : item.icon} />
              <span>{item.label}</span>
            </>
          );
          return (
            <li key={item.key} onClick={() => handleMenuItemClick(item)}>
              {isMailto ? (
                <a href={item.route} className={className}>
                  {content}
                </a>
              ) : (
                <Link className={className} to={item.route}>
                  {content}
                </Link>
              )}
            </li>
          );
        })}
        {device === DEVICE_MOBILE && (
          <>
            {!isAppOnlyCorridorRoute && !(hard_sme_migrated || smeFlow) && (
              <>
                <MobileDivider />
                <li
                  onClick={() => {
                    logCTEvents('Dashboard', EVENT_FLOW_TYPE.DASHBOARD);
                    onAccountLinkClick(DASHBOARD_URL);
                  }}
                >
                  <Link className="nav-list__item" to={DASHBOARD_URL}>
                    <img alt="dashboard" src={SEND_MONEY_ICON} />
                    <span>Dashboard</span>
                  </Link>
                </li>
                <li>
                  <Link
                    className={`nav-list__item${!authorized ? ' disabled' : ''}`}
                    to={USER_PROFILE_URL}
                    onClick={(e) => {
                      if (!authorized) return e.preventDefault();
                      onAccountLinkClick();
                    }}
                  >
                    <img alt="user-profile" src={RECIPIENT_ICON} />
                    <span>My info</span>
                  </Link>
                </li>
                {status === APPROVED &&
                  ((showManagePayment.includes(country_code) && account_type === INDIVIDUAL) ||
                    (showManagePaymentCorporate.includes(country_code) &&
                      account_type === CORPORATE)) &&
                  paymentOptionTabVisible && (
                    <li onClick={() => onAccountLinkClick()}>
                      <Link className="nav-list__item" to={MANAGE_PAYMENT_OPTION_URL}>
                        <img alt="manage payment" src={MANAGE_PAYMENT_OPT_ICON} />
                        <span>Manage payment options</span>
                      </Link>
                    </li>
                  )}
                <li onClick={() => onAccountLinkClick()}>
                  <Link className="nav-list__item" to={SUBSCRIPTION_URL}>
                    <img alt="subscription" src={SUBSCRIPTION_ICON} />
                    <span>Subscription</span>
                  </Link>
                </li>
                {status !== APPROVED && account_type !== CORPORATE && (
                  <li onClick={() => onAccountLinkClick()}>
                    <Link className="nav-list__item" to={REFERRAL_URL}>
                      <img alt="referral" src={REFERRAL_SMALL_ICON} />
                      <span>Referrals</span>
                    </Link>
                  </li>
                )}
              </>
            )}
            <li className="nav-list__item" onClick={logout}>
              <img alt="logout" src={LOGOUT_ICON} />
              <span>{LOGOUT_LABEL}</span>
            </li>
          </>
        )}
      </NavList>
    </SideNavContainer>
  );
};

export default SideNav;
